import { type Dispatch, bindActionCreators } from "@reduxjs/toolkit";
import cn from "classnames";
import { scaleLinear } from "d3";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { type ConnectedProps, connect } from "react-redux";
import { type RootState } from "../../../Application/globaltypes/redux";

import { timeScale } from "components/charts/shared/bsi-time-scale";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { FeatureFlags } from "featureFlags";
import { RStatus } from "features/Application/globaltypes/fetchRequest";
import { setExportAction } from "features/Reporting/state/export/exportSlice";
import { reset, setIsNavigationEnabled, setIsReportEnabled } from "features/Reporting/state/toolbar/toolbarSlice";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { bindAction } from "interfaces";
import CardReporting, { type CardReportingItem } from "../../../../components/cards/CardReporting/CardReporting";
import { ChartWrapper, HorizontalBarChart, LineChart, getDateFormatByCount } from "../../../../components/charts";
import { RequestStatusRenderer } from "../../../../components/requestStatsRenderer/RequestStatusRenderer";
import { type RegisterBreadcrumbCallback } from "../../Common/Hooks/usePerformanceBreadcrumbs";
import {
  chartColorScale,
  chartLegendLabels,
  completes,
  lineChartMargins,
  dailyActivity,
  lineChartTitles,
  noBarData,
  noData,
  starts,
  totalActivity,
  transitionTime,
  validLineData,
  createDateRange,
  type PerformanceWithAccountFilter,
  normalizeFlowFilter,
} from "../../Common/utils/performanceUtils";
import { reportingColors } from "features/Library/Common/utils/constants";
import {
  resetAnswerData,
  selectEngagementCompleted,
  selectEngagementCumulativeCompleted,
  selectEngagementCumulativeInProgress,
  selectEngagementDates,
  selectEngagementFunnelData,
  selectEngagementFunnelValue,
  selectEngagementInProgress,
  selectEngagementLineData,
  selectEngagementQuestionsValue,
  selectPeopleDetailsStatus,
  selectPeopleDetailsValue,
} from "../state/slices/assessmentPerformanceSlice";
import * as assessmentPerformanceActions from "../state/thunks/assessmentPerformanceThunk";
import { type AssessmentQuestionAnswer, type EngagementQuestionsData } from "../types/state";
import AssessmentPerformancePeopleComponent from "./AssessmentPerformancePeople";
import AssessmentPerformanceDrillDown from "./Drilldown/AssessmentPerformanceDrillDown";
import AssessmentPerformanceList from "./List/AssessmentPerformanceList";

import "../../Common/utils/performanceSCSSUtils.scss";
import "./AssessmentPerformance.scss";
import {
  sharedAccountReportingHorizontalBarProps,
  sharedAccountReportingLineProps,
} from "features/Reporting/Content/shared";
import { CHART_PERIOD, useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";

export interface Props extends PropsFromRedux {
  entityId: number;
  entityTitle: string;
  dateFilter: PerformanceWithAccountFilter;
  registerBreadcrumb: RegisterBreadcrumbCallback;
  flowId?: number;
  hideDrilldownHeader?: boolean;
  includeAccounts?: boolean;
  childAccountId?: number;
  hasCreateAccountsAddOn?: boolean;
}

export type cardsDataFormatted = {
  PassingGrade: string;
  AverageGrade: string;
  AverageTime: string;
  CompletedCount: number;
};

const getSelectedQuestion = (
  selectedQuestion: EngagementQuestionsData | undefined,
  engagementQuestionsData: EngagementQuestionsData[],
  setSelectedQuestionIndex: (value: React.SetStateAction<number>) => void,
) => {
  if (selectedQuestion) {
    const index = engagementQuestionsData.findIndex((question) => question.QuestionId === selectedQuestion.QuestionId);
    setSelectedQuestionIndex(index);
  }
};

export const AssessmentPerformanceBody: FC<Props> = ({
  entityId,
  entityTitle,
  flowId,
  dateFilter,
  registerBreadcrumb,
  hideDrilldownHeader,
  actions,
  setExportAction,
  includeAccounts,
  childAccountId,
  hasCreateAccountsAddOn,

  engagementLineStatus,

  engagementDates,
  engagementInProgress,
  engagementCompleted,
  lineCumulativeCompleted,
  lineCumulativeInProgress,

  engagementFunnelData,
  engagementFunnelStatus,

  engagementQuestionsData,

  resetSelectedAnswerState,
  setIsReportEnabled,
  resetIsReportEnabled,
  setIsNavigationEnabled,
}) => {
  const [newLineChartState, setNewLineChartState] = useState(totalActivity);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
  const [selectedQuestion, setSelectedQuestion] = useState<EngagementQuestionsData>();
  const [selectedAnswer, setSelectedAnswer] = useState<AssessmentQuestionAnswer>();
  const reportEnabled = useFeatureFlag(FeatureFlags.AssessmentReport);

  const effectiveFlowId = normalizeFlowFilter(flowId ?? dateFilter.flowId);

  const dateRange = useMemo(
    () => createDateRange(dateFilter.dateFrom, dateFilter.dateTo),
    [dateFilter.dateFrom, dateFilter.dateTo],
  );

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const horizontalBarData = useMemo(
    () => [
      {
        id: "started-bar",
        category: starts,
        value: engagementFunnelData.barData.Started ?? 0,
        fillColor: reportingColors.blue,
      },
      {
        id: "completed-bar",
        category: completes,
        value: engagementFunnelData.barData.Completed ?? 0,
        fillColor: reportingColors.yellow,
      },
    ],
    [engagementFunnelData],
  );

  const lineInfo = useMemo(() => {
    const yData =
      newLineChartState === dailyActivity
        ? [engagementInProgress, engagementCompleted]
        : [lineCumulativeInProgress, lineCumulativeCompleted];
    return {
      xData: [engagementDates, engagementDates],
      yData,
      yFormatterFunc: ",d",
      yScaleRatio: scaleLinear,
      colorRange: chartColorScale,
    };
  }, [
    engagementCompleted,
    engagementDates,
    engagementInProgress,
    lineCumulativeCompleted,
    lineCumulativeInProgress,
    newLineChartState,
  ]);

  useEffect(() => {
    if (reportEnabled && chartPeriod !== CHART_PERIOD.UNSET && dateFilter.type !== undefined) {
      actions.fetchEngagementLineData(entityId, { ...dateFilter, aggregation: chartPeriod, flowId: effectiveFlowId });
    }
  }, [actions, dateFilter, entityId, effectiveFlowId, reportEnabled, chartPeriod]);

  useEffect(() => {
    if (reportEnabled && dateFilter.type !== undefined) {
      actions.fetchEngagementFunnelData(entityId, { ...dateFilter, flowId: effectiveFlowId });
      actions.fetchPeopleDetails(entityId, { ...dateFilter, flowId: effectiveFlowId });
    }
  }, [actions, dateFilter, entityId, effectiveFlowId, reportEnabled]);

  useEffect(() => {
    setExportAction({
      method: actions.doExport,
      args: [entityId, { ...dateFilter, flowId: effectiveFlowId }, entityTitle],
      isExportEnabled: true,
    });
  }, [actions.doExport, dateFilter, entityId, selectedAnswer, effectiveFlowId, setExportAction, entityTitle]);

  const breadCrumbCallback = useCallback(() => {
    setSelectedQuestionIndex(0);
    setSelectedQuestion(undefined);
    setSelectedAnswer(undefined);
  }, []);

  useEffect(
    () => getSelectedQuestion(selectedQuestion, engagementQuestionsData, setSelectedQuestionIndex),
    [selectedQuestion, engagementQuestionsData],
  );

  const handleQuestionSelection = useCallback(
    (question: EngagementQuestionsData) => {
      registerBreadcrumb({ text: question.QuestionText, action: breadCrumbCallback });
      setSelectedQuestion(question);
    },
    [breadCrumbCallback, registerBreadcrumb],
  );

  const selectionExists = !!selectedQuestion;

  useEffect(() => {
    setIsNavigationEnabled(!selectedQuestion);
  }, [selectedQuestion, setIsNavigationEnabled]);

  const getPrevQuestion = () => {
    const previousIndex = selectedQuestionIndex - 1;
    const wrappedIndex = (previousIndex + engagementQuestionsData.length) % engagementQuestionsData.length;
    setSelectedQuestionIndex(wrappedIndex);
    setSelectedQuestion(engagementQuestionsData[wrappedIndex]);
    setSelectedAnswer(undefined);
    resetSelectedAnswerState();
  };

  const getNextQuestion = () => {
    const nextIndex = selectedQuestionIndex + 1;
    const wrappedIndex = nextIndex % engagementQuestionsData.length;
    setSelectedQuestionIndex(wrappedIndex);
    setSelectedQuestion(engagementQuestionsData[wrappedIndex]);
    setSelectedAnswer(undefined);
    resetSelectedAnswerState();
  };

  useEffect(() => {
    setIsReportEnabled(reportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [reportEnabled, setIsReportEnabled, resetIsReportEnabled]);

  const barChart = useMemo(() => {
    if (noBarData(...horizontalBarData.map((d) => d.value))) {
      return noData(dateFilter);
    }
    return <HorizontalBarChart {...sharedAccountReportingHorizontalBarProps} data={horizontalBarData} />;
  }, [dateFilter, horizontalBarData]);

  const renderMain = useCallback(() => {
    return (
      <div className="body">
        <div className="chartsSection">
          <div className="graphs">
            <div className={cn("lineChartContainer", "user_chart_container")} ref={measureRef}>
              <ChartWrapper
                showLegend
                titles={lineChartTitles}
                legendLabels={chartLegendLabels}
                onChange={setNewLineChartState}
                colorRange={chartColorScale}
              >
                <RequestStatusRenderer state={engagementLineStatus}>
                  {validLineData([...lineInfo.yData]) ? (
                    <LineChart
                      margins={lineChartMargins}
                      xScaleRatio={timeScale}
                      xFormatterFunc={getDateFormatByCount(engagementDates.length)}
                      transitionDuration={transitionTime}
                      {...lineInfo}
                    />
                  ) : (
                    noData(dateFilter)
                  )}
                </RequestStatusRenderer>
              </ChartWrapper>
            </div>
            <div className={cn("funnelChartContainer", "user_chart_container")}>
              <ChartWrapper titles={["Engagement"]}>
                <RequestStatusRenderer state={engagementFunnelStatus}>{barChart}</RequestStatusRenderer>
              </ChartWrapper>
            </div>
          </div>
          <div className="cardSection">
            <RequestStatusRenderer state={engagementFunnelStatus}>
              <CardReporting
                items={
                  [
                    {
                      statDescription: "Passing Grade",
                      stat: engagementFunnelData.cardData.PassingGrade,
                      isStatPercentage: true,
                      popupHeader: "Passing Grade",
                      popupBody: "Grade needed to pass assessment",
                      width: "8rem",
                    },
                    {
                      statDescription: "Average Grade",
                      stat: engagementFunnelData.cardData.AverageGrade,
                      isStatPercentage: engagementFunnelData.cardData.AverageGrade !== "-",
                      popupHeader: "Average Grade",
                      popupBody: "Average user grade for completed assessments",
                      width: "11rem",
                    },
                    {
                      statDescription: "Average Time",
                      stat: engagementFunnelData.cardData.AverageTime,
                      popupHeader: "Average Time",
                      popupBody: "Average user time to complete the assessment",
                      width: "14.2rem",
                    },
                    {
                      statDescription: completes,
                      stat: engagementFunnelData.cardData.CompletedCount,
                      popupHeader: `Total ${completes}`,
                      popupBody: "Total number of times users have completed the assessment",
                      width: "13.75rem",
                    },
                  ] as CardReportingItem[]
                }
              />
            </RequestStatusRenderer>
          </div>
          <div className="tableSection">
            <AssessmentPerformanceList
              hideControls
              entityId={entityId}
              flowId={effectiveFlowId}
              selectQuestion={handleQuestionSelection}
              selectAnswer={setSelectedAnswer}
              dateFilter={dateFilter}
              hasData={
                engagementFunnelStatus.status === RStatus.Got && !noBarData(...horizontalBarData.map((d) => d.value))
              }
            />
          </div>
          <div className="performanceTableSection">
            <AssessmentPerformancePeopleComponent
              includeAccounts={includeAccounts}
              filter={dateFilter}
              childAccountId={childAccountId}
              hasCreateAccountsAddOn={hasCreateAccountsAddOn}
            />
          </div>
        </div>
      </div>
    );
  }, [
    barChart,
    dateFilter,
    engagementDates.length,
    engagementFunnelStatus,
    engagementLineStatus,
    entityId,
    effectiveFlowId,
    handleQuestionSelection,
    horizontalBarData,
    lineInfo,
    includeAccounts,
    childAccountId,
    hasCreateAccountsAddOn,
    measureRef,
    engagementFunnelData,
  ]);

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return selectionExists ? (
    <AssessmentPerformanceDrillDown
      includeAccounts={includeAccounts}
      childAccountId={childAccountId}
      hasCreateAccountsAddOn={hasCreateAccountsAddOn}
      assessmentId={entityId}
      flowId={effectiveFlowId}
      selectedQuestionIndex={selectedQuestionIndex}
      totalQuestionCount={engagementQuestionsData.length}
      dateFilter={dateFilter}
      hideHeader={hideDrilldownHeader}
      selectedQuestion={selectedQuestion}
      selectedAnswer={selectedAnswer}
      onSelectAnswer={setSelectedAnswer}
      onNextQuestion={getNextQuestion}
      onPrevQuestion={getPrevQuestion}
    />
  ) : (
    renderMain()
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  return {
    engagementLineStatus: selectEngagementLineData(state),
    engagementDates: selectEngagementDates(state),
    engagementInProgress: selectEngagementInProgress(state),
    engagementCompleted: selectEngagementCompleted(state),
    lineCumulativeCompleted: selectEngagementCumulativeCompleted(state),
    lineCumulativeInProgress: selectEngagementCumulativeInProgress(state),
    engagementFunnelStatus: selectEngagementFunnelData(state),
    engagementFunnelData: selectEngagementFunnelValue(state),

    engagementQuestionsData: selectEngagementQuestionsValue(state),

    peopleDetailsStatus: selectPeopleDetailsStatus(state),
    peopleDetailsData: selectPeopleDetailsValue(state),
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    actions: bindActionCreators(assessmentPerformanceActions, dispatch),
    resetSelectedAnswerState: bindAction(resetAnswerData, dispatch),
    setExportAction: bindAction(setExportAction, dispatch),
    setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
    resetIsReportEnabled: bindAction(reset, dispatch),
    setIsNavigationEnabled: bindAction(setIsNavigationEnabled, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(AssessmentPerformanceBody);
export default ConnectedComponent;
